import { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../ui/Card';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { Skeleton } from '../ui/Skeleton';
import { fetchCityVenueRanking, type VenueRankingItem, type VenueRankingMetric } from './venueRankingApi';

type VenueRankingCardProps = {
  cityId: string;
  metric: VenueRankingMetric;
  title: string;
  onOpenVenue: (venueId: string) => void;
  limit?: number;
  mobileSwitch?: boolean;
};

function RankingSkeleton({ rows }: { rows: number }) {
  return (
    <div className="pulse-venue-ranking-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => <Skeleton key={index} height={44} />)}
    </div>
  );
}

function VenueAvatar({ url }: { url: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return url && url !== failedUrl
    ? <img src={url} alt="" className="pulse-venue-ranking-avatar" onError={() => setFailedUrl(url)} />
    : <span className="pulse-venue-ranking-avatar is-empty" aria-hidden="true"><HopperIcon name="building" size={18} /></span>;
}

function score(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);
}

export function VenueRankingCard({
  cityId,
  metric,
  title,
  onOpenVenue,
  limit = 5,
  mobileSwitch = false,
}: VenueRankingCardProps) {
  const [selectedMetric, setSelectedMetric] = useState<VenueRankingMetric>(metric);
  const activeMetric = mobileSwitch ? selectedMetric : metric;
  const [items, setItems] = useState<VenueRankingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const requestSequence = useRef(0);

  useEffect(() => {
    if (!mobileSwitch) setSelectedMetric(metric);
  }, [metric, mobileSwitch]);

  const load = useCallback(async () => {
    if (!cityId) return;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError(false);
    try {
      const response = await fetchCityVenueRanking(cityId, activeMetric);
      if (requestId !== requestSequence.current) return;
      setItems(response.items);
    } catch {
      if (requestId !== requestSequence.current) return;
      setError(true);
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [activeMetric, cityId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Card className={`pulse-venue-ranking-card${mobileSwitch ? ' is-mobile-switch' : ''}`} aria-label={title}>
      <div className="pulse-side-heading pulse-venue-ranking-heading">
        <h2>{title}</h2>
        {mobileSwitch && (
          <div className="pulse-ranking-metric-tabs" role="tablist" aria-label="Метрика рейтинга заведений">
            <button
              type="button"
              role="tab"
              aria-selected={selectedMetric === 'live_score'}
              className={selectedMetric === 'live_score' ? 'is-active' : ''}
              onClick={() => setSelectedMetric('live_score')}
            >
              Live Score
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={selectedMetric === 'hopper_score'}
              className={selectedMetric === 'hopper_score' ? 'is-active' : ''}
              onClick={() => setSelectedMetric('hopper_score')}
            >
              Hopper Score
            </button>
          </div>
        )}
      </div>
      {!loaded && loading ? <RankingSkeleton rows={limit} /> : error ? (
        <div className="pulse-side-empty"><strong>Рейтинг не загрузился</strong><button type="button" onClick={() => void load()}>Повторить</button></div>
      ) : items.length === 0 ? (
        <div className="pulse-side-empty" aria-busy={loading}>Пока нет данных рейтинга.</div>
      ) : (
        <ol className="pulse-venue-ranking-list" aria-busy={loading}>
          {items.slice(0, limit).map((item, index) => (
            <li key={item.id}>
              <button type="button" className="pulse-venue-ranking-row" onClick={() => onOpenVenue(item.id)}>
                <span className="pulse-venue-ranking-rank">{index + 1}</span>
                <VenueAvatar url={item.avatar_url} />
                <span className="pulse-venue-ranking-name">{item.name?.trim() || 'Заведение'}</span>
                <strong className="pulse-venue-ranking-score">{score(item.score_value)}</strong>
              </button>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
